import React, { useRef, useEffect } from "react";
import * as Mui from "@mui/material";
import { themes, scrollTop, optionalAvathar } from "services/constants";
import { TableEmptyState } from "components/CustomComponents/EmptyState";

const EMPTY_FALLBACK_KEYS = new Set([
  "user_name",
  "employee_id",
  "total_hours",
  "fixed_salary",
  "paid_hours",
]);
const TEXT_ONLY_KEYS = new Set(["employee_id", "esi_no", "pf_no", "uan_no"]);
// These are only meaningful for Hours-basis payrun rows; the row builder
// sends "" (not 0) for non-Hours-basis rows, so blank them instead of
// falling back to "00:00" the way a genuine zero would.
const HOURS_BASIS_ONLY_KEYS = new Set([
  "payable_hours",
  "ot_hours",
  "weekoff_hours",
  "holiday_hours",
]);

const formatAsHhMm = (value) => {
  if (value === null || value === undefined || value === "") {
    return "00:00";
  }

  const hhMmMatch = /^(\d{1,4}):(\d{1,2})$/.exec(`${value}`.trim());
  if (hhMmMatch) {
    const hours = Number(hhMmMatch[1]);
    const minutes = Number(hhMmMatch[2]);
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
  }

  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return "00:00";
  }

  const totalMinutes = Math.round(numericValue * 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
};

const formatValue = (value) => {
  if (value || value === 0) {
    if (typeof value === "number") {
      return value.toFixed(2);
    }

    const parsedValue = Number(value);
    if (!Number.isNaN(parsedValue) && value !== "") {
      return parsedValue.toFixed(2);
    }

    return value;
  }

  return "N/A";
};

const formatCellValue = (key, value) => {
  if (
    HOURS_BASIS_ONLY_KEYS.has(key) &&
    (value === null || value === undefined || value === "")
  ) {
    return "";
  }

  if (
    ["paid_hours", "payable_hours", "ot_hours", "weekoff_hours", "holiday_hours"].includes(key)
  ) {
    return formatAsHhMm(value);
  }

  if (
    EMPTY_FALLBACK_KEYS.has(key) &&
    (value === null || value === undefined || value === "")
  ) {
    return "";
  } 

  if (TEXT_ONLY_KEYS.has(key)) {
    return value || value === 0 ? value : "N/A";
  }

  return formatValue(value);
};

export const SalaryStatementTable = ({
  salaryDetails,
  listViewPage,
  setListViewPage,
  dataLength,
  rowsPerPage,
  setRowsPerPage,
  reportType,
  employeeWiseHeaders = [],
  summaryHeaders = [],
  detailedHeaders = [],
}) => {
  const containerRef = useRef(null);

  const handleChangePage = (event, newPage) => {
    setListViewPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setListViewPage(0);
  };

  useEffect(() => {
    scrollTop(containerRef);
  }, [listViewPage]);

  const paginatedRows = salaryDetails?.slice(
    listViewPage * rowsPerPage,
    listViewPage * rowsPerPage + rowsPerPage,
  );

  const renderEmployeeWiseTable = () => (
    <Mui.Table
      className="border"
      sx={{
        tableLayout: "fixed",
        width: "max-content",
        minWidth: "100%",
      }}
    >
      <Mui.TableHead
        sx={{
          backgroundColor: themes.primary,
          position: "sticky",
          top: -1,
          zIndex: 10,
          "& .MuiTableCell-root": {
            height: 40,
            py: 0.5,
            lineHeight: 1.2,
          },
        }}
      >
        <Mui.TableRow>
          {employeeWiseHeaders.map((header) => (
            <Mui.TableCell
              key={header.key}
              sx={{
                ...(header?.name === "Employee Name"
                  ? {
                      color: themes.headLine,
                      fontWeight: "bold",
                      position: "sticky",
                      left: -1,
                      backgroundColor: themes.primary,
                      zIndex: 2,
                    }
                  : {
                      color: themes.headLine,
                      fontWeight: "bold",
                    }),
              }}
            >
              {header.name}
            </Mui.TableCell>
          ))}
        </Mui.TableRow>
      </Mui.TableHead>
      <Mui.TableBody>
        {salaryDetails?.length === 0 ? (
          <TableEmptyState dataLength={dataLength} />
        ) : (
          paginatedRows?.map((salary) => {
            const isGrandTotal = salary?.rowType === "grand_total";

            return (
              <Mui.TableRow
                key={salary?.id || salary?.employee_id}
                sx={
                  isGrandTotal
                    ? {
                        backgroundColor: "#f4f8ff",
                        "& .MuiTableCell-root": { fontWeight: 700 },
                      }
                    : undefined
                }
              >
                {employeeWiseHeaders.map((header) => {
                  const cellValue = salary?.[header.key];
                  const isEmptyTotalCell =
                    isGrandTotal &&
                    header.key !== "user_name" &&
                    (cellValue === null ||
                      cellValue === undefined ||
                      cellValue === "");

                  return (
                    <Mui.TableCell
                      key={header.key}
                      sx={{
                        ...(header?.key === "user_name"
                          ? {
                              position: "sticky",
                              left: -1,
                              backgroundColor: isGrandTotal
                                ? "#f4f8ff"
                                : themes.primary,
                              zIndex: 1,
                              color: themes.headLine,
                            }
                          : { color: themes.headLine }),
                      }}
                    >
                      {header.key === "user_name" ? (
                        isGrandTotal ? (
                          "Total"
                        ) : (
                          <Mui.Stack direction="row" alignItems="center">
                            <Mui.Avatar
                              sx={{
                                width: 23,
                                height: 23,
                              }}
                              alt={salary?.user_name?.charAt(0).toUpperCase()}
                              src={salary?.image_url ?? optionalAvathar}
                            />
                            &nbsp;
                            {salary?.user_name}
                          </Mui.Stack>
                        )
                      ) : isEmptyTotalCell ? (
                        ""
                      ) : (
                        formatCellValue(header.key, cellValue)
                      )}
                    </Mui.TableCell>
                  );
                })}
              </Mui.TableRow>
            );
          })
        )}
      </Mui.TableBody>
    </Mui.Table>
  );

  const renderSummaryTable = () => (
    <Mui.Table
      className="border"
      sx={{ width: "max-content", minWidth: "100%" }}
    >
      <Mui.TableHead
        sx={{
          backgroundColor: themes.primary,
          position: "sticky",
          top: -1,
          zIndex: 10,
        }}
      >
        <Mui.TableRow>
          {summaryHeaders.map((header) => (
            <Mui.TableCell
              key={header.key}
              sx={{ color: themes.headLine, fontWeight: "bold" }}
            >
              {header.name}
            </Mui.TableCell>
          ))}
        </Mui.TableRow>
      </Mui.TableHead>
      <Mui.TableBody>
        {salaryDetails?.length === 0 ? (
          <TableEmptyState dataLength={dataLength} />
        ) : (
          paginatedRows?.map((row, index) => (
            <Mui.TableRow
              key={row?.id || `${row?.department}-${index}`}
              sx={
                row?.rowType === "grand_total"
                  ? {
                      backgroundColor: "#f4f8ff",
                      "& .MuiTableCell-root": {
                        fontWeight: 700,
                      },
                    }
                  : undefined
              }
            >
              {summaryHeaders.map((header) => (
                <Mui.TableCell key={header.key} sx={{ color: themes.headLine }}>
                  {formatCellValue(header.key, row?.[header.key])}
                </Mui.TableCell>
              ))}
            </Mui.TableRow>
          ))
        )}
      </Mui.TableBody>
    </Mui.Table>
  );

  const renderDetailedTable = () => (
    <Mui.Table
      className="border"
      sx={{ width: "max-content", minWidth: "100%" }}
    >
      <Mui.TableHead
        sx={{
          backgroundColor: themes.primary,
          position: "sticky",
          top: -1,
          zIndex: 10,
        }}
      >
        <Mui.TableRow>
          {detailedHeaders.map((header) => (
            <Mui.TableCell
              key={header.key}
              sx={{ color: themes.headLine, fontWeight: "bold" }}
            >
              {header.name}
            </Mui.TableCell>
          ))}
        </Mui.TableRow>
      </Mui.TableHead>
      <Mui.TableBody>
        {salaryDetails?.length === 0 ? (
          <TableEmptyState dataLength={dataLength} />
        ) : (
          paginatedRows?.map((row, index) => {
            if (row?.rowType === "department_header") {
              return (
                <Mui.TableRow key={row?.id || `department-${index}`}>
                  <Mui.TableCell
                    colSpan={detailedHeaders.length}
                    sx={{
                      fontWeight: 700,
                      color: themes.headLine,
                      backgroundColor: "#eef5ff",
                    }}
                  >
                    {row?.department}
                  </Mui.TableCell>
                </Mui.TableRow>
              );
            }

            if (row?.rowType === "department_total") {
              return (
                <Mui.TableRow
                  key={row?.id || `department-total-${index}`}
                  sx={{
                    backgroundColor: "#f8fafc",
                    "& .MuiTableCell-root": {
                      fontWeight: 700,
                    },
                  }}
                >
                  {detailedHeaders.map((header, headerIndex) => (
                    <Mui.TableCell
                      key={header.key}
                      sx={{ color: themes.headLine }}
                    >
                      {headerIndex === 0
                        ? "Subtotal"
                        : row?.[header.key] === null ||
                            row?.[header.key] === undefined ||
                            row?.[header.key] === ""
                          ? ""
                          : formatCellValue(header.key, row?.[header.key])}
                    </Mui.TableCell>
                  ))}
                </Mui.TableRow>
              );
            }

            return (
              <Mui.TableRow
                key={row?.id || row?.employee_id || `employee-row-${index}`}
              >
                {detailedHeaders.map((header) => (
                  <Mui.TableCell
                    key={header.key}
                    sx={{ color: themes.headLine }}
                  >
                    {formatCellValue(header.key, row?.[header.key])}
                  </Mui.TableCell>
                ))}
              </Mui.TableRow>
            );
          })
        )}
      </Mui.TableBody>
    </Mui.Table>
  );

  return (
    <React.Fragment>
      <Mui.Grid container>
        <Mui.TableContainer
          ref={containerRef}
          sx={{ width: "100%", maxHeight: 550 }}
        >
          {reportType === "summarized"
            ? renderSummaryTable()
            : reportType === "detailed"
              ? renderDetailedTable()
              : renderEmployeeWiseTable()}
        </Mui.TableContainer>
      </Mui.Grid>

      {salaryDetails?.length > 25 && (
        <Mui.TablePagination
          className="custom-pagination"
          component="div"
          rowsPerPageOptions={[25, 50, 75, 100]}
          count={salaryDetails?.length}
          rowsPerPage={rowsPerPage}
          page={listViewPage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      )}
    </React.Fragment>
  );
};
